import { Col, Form, Input, Row, notification } from 'antd';
import { filterDigits, localize, removeEmptyValues } from 'mixins/helpers';
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { getTaxPayerInfo } from '../actions';
import { IFindTaxpayerServiceProps } from '../find-taxpayer-service.type';

const FindTaxpayerServiceJur = forwardRef((props: IFindTaxpayerServiceProps, ref) => {
    const { beginDate, endDate, statusName, organizationName } = props.config;
    const [form] = Form.useForm();
    const [status, setStatus] = useState<string>();

    const formValues = Form.useWatch([], form);

    // Effect to prevent stale validation errors when field
    // becomes optional depending on another field value
    useEffect(() => {
        form.validateFields();
    }, [formValues]);

    const onFinish = (values: any) => {
        props.setLoading(true);
        let formattedValues = removeEmptyValues(Object.assign(values, { type: props.type }));
        props.setValues(formattedValues);
        getTaxPayerInfo(formattedValues)
            .then((value) => {
                // Check if taxpayer type matches the selected type
                if (value.data.taxpayers && value.data.taxpayers.length > 0) {
                    const taxpayerType = value.data.taxpayers[0].type;
                    if (props.type !== taxpayerType) {
                        notification['error']({
                            message: localize('error', props.translations),
                            description: localize(
                                'find-taxpayer-service-type-not-match',
                                props.translations
                            ),
                        });
                        return;
                    }
                }
                props.setTableData(value.data.taxpayers);
                props.setTotal(value.data.total);

                if (formattedValues.type === 'UL') {
                    const answerDto = value.data.answerDto;
                    if (!answerDto) {
                        notification['warning']({
                            message: localize(
                                'find-taxpayer-service-payments-not-found',
                                props.translations
                            ),
                        });
                    }

                    props.setPaymentData(
                        answerDto
                            ? {
                                taxRevenue: {
                                    overall: answerDto.overall ?? {},
                                },
                                ndsRevenue: {
                                    tax: answerDto.ndsType ?? {},
                                },
                            }
                            : null
                    );
                }
            })
            .finally(() => props.setLoading(false));
    };

    const handleSelectChange = (value: string) => {
        setStatus(value);
    };

    useImperativeHandle(ref, () => ({
        clearForm() {
            form.resetFields();
            props.setTableData(null);
            props.setValues(null);
            props.setTotal(null);
        },
        searchForm() {
            form.submit();
        },
    }));

    useEffect(() => {
        form.validateFields(['organizationName']);
    }, [props.type]);

    return (
        <>
            <Form
                name="findTaxpayerJur"
                onFinish={onFinish}
                form={form}
                layout="vertical"
            >
                <Row gutter={{ xs: 8, md: 16 }}>
                    <Col
                        xs={24}
                        md={8}
                    >
                        <Form.Item
                            label={props.iinBinLabel(props.type)}
                            name="iinBin"
                            rules={[
                                {
                                    required:
                                        props.type === 'UL' ? true : !formValues?.organizationName,
                                    message: localize(
                                        'error.field-is-required',
                                        props.translations
                                    ),
                                },
                                {
                                    min: 12,
                                    message: localize(
                                        'error.iin-bin-length-12',
                                        props.translations
                                    ),
                                },
                            ]}
                        >
                            <Input
                                onChange={filterDigits(form, 'iinBin')}
                                maxLength={12}
                                placeholder={localize('placeholder.input', props.translations)}
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={{ xs: 8, md: 16 }}>
                    <Col
                        xs={24}
                        md={10}
                    >
                        <Form.Item
                            key={props.type}
                            label={
                                props.type === 'IP'
                                    ? localize('ip-name', props.translations)
                                    : organizationName
                            }
                            name="organizationName"
                            rules={[
                                {
                                    required: props.type === 'UL' ? false : !formValues?.iinBin,
                                    message: localize(
                                        'error.field-is-required',
                                        props.translations
                                    ),
                                },
                                {
                                    min: 3,
                                    message: localize('error.unique-code-3', props.translations),
                                },
                            ]}
                        >
                            <Input
                                placeholder={localize('placeholder.input', props.translations)}
                            />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </>
    );
});

export default FindTaxpayerServiceJur;
